import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useIdleTimer as useIdleTimerLib } from 'react-idle-timer';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

// Default timeout values (in milliseconds)
const DEFAULT_IDLE_TIMEOUT = 15 * 60 * 1000; // 15 minutes
const DEFAULT_PROMPT_TIMEOUT = 60 * 1000; // 1 minute countdown

// Unique name for this idle timer instance across tabs
const IDLE_TIMER_NAME = 'tempus-irb-idle-timer';

interface IdleTimerContextType {
  isPromptOpen: boolean;
  remainingTime: number;
  resetTimer: () => void;
  confirmActivity: () => void;
}

const IdleTimerContext = createContext<IdleTimerContextType | undefined>(undefined);

export const IdleTimerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  // Safeguard: Only show prompt and process idle events if user is logged in
  const handlePrompt = useCallback(() => {
    // Skip if no authenticated user
    if (!user) {
      console.debug('Idle timer: Prompt suppressed (no authenticated user)');
      return;
    }
    
    console.debug('Idle timer: Prompt triggered');
    setRemainingTime(Math.ceil(DEFAULT_PROMPT_TIMEOUT / 1000));
    setIsPromptOpen(true);
  }, [user]);

  const handleIdle = useCallback(async () => {
    // Skip if no authenticated user
    if (!user) {
      console.debug('Idle timer: Idle event suppressed (no authenticated user)');
      return;
    }
    
    console.debug('Idle timer: User idle, logging out');
    setIsPromptOpen(false);
    setRemainingTime(0);
    
    try {
      await signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error during automatic logout:', error);
    }
  }, [signOut, navigate, user]);

  const handleActive = useCallback(() => {
    // Skip if no authenticated user
    if (!user) {
      console.debug('Idle timer: Active event suppressed (no authenticated user)');
      return;
    }
    
    console.debug('Idle timer: User active');
    setIsPromptOpen(false);
    setRemainingTime(0);
  }, [user]);

  // Handle messages from other tabs
  const handleMessage = useCallback((message: any) => {
    console.debug('Idle timer: Received message from another tab', message);
  }, []);

  const { reset, getRemainingTime } = useIdleTimerLib({
    timeout: DEFAULT_IDLE_TIMEOUT,
    promptBeforeIdle: DEFAULT_PROMPT_TIMEOUT,
    onPrompt: handlePrompt,
    onIdle: handleIdle,
    onActive: handleActive,
    debounce: 500,
    // Cross-tab support
    crossTab: true,
    syncTimers: 10000,
    name: IDLE_TIMER_NAME,
    onMessage: handleMessage,
    // Disable timer when no user is authenticated
    disabled: !user
  });

  const confirmActivity = useCallback(() => {
    if (!user) return;
    
    console.debug('Idle timer: Activity confirmed by user');
    reset();
    setIsPromptOpen(false);
    setRemainingTime(0);
  }, [reset, user]);

  // Update the countdown timer
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    
    if (isPromptOpen) {
      interval = setInterval(() => {
        const remaining = Math.ceil(getRemainingTime() / 1000);
        setRemainingTime(remaining);
        
        if (remaining <= 0) {
          clearInterval(interval);
        }
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPromptOpen, getRemainingTime]);

  const resetTimer = useCallback(() => {
    if (!user) return;
    
    console.debug('Idle timer: Manual timer reset');
    reset();
  }, [reset, user]);

  return (
    <IdleTimerContext.Provider
      value={{
        isPromptOpen,
        remainingTime,
        resetTimer,
        confirmActivity
      }}
    >
      {children}
    </IdleTimerContext.Provider>
  );
};

export const useIdleTimerContext = (): IdleTimerContextType => {
  const context = useContext(IdleTimerContext);
  if (context === undefined) {
    throw new Error('useIdleTimerContext must be used within an IdleTimerProvider');
  }
  return context;
}; 
